import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';

import { updateAliasinLayoutContent } from 'actions/layoutManager';
import { fetchAliasesForAsset } from 'actions/assetAliases';

import {
  Button, OverlayTrigger, Popover, Image, Spinner, Dropdown, Form, Badge
} from 'react-bootstrap';

import _ from 'lodash';
import cx from 'classnames';
import Truncate from 'react-truncate';
import Tooltip from 'components/Tooltip';
import { IKImage } from "imagekitio-react";
import moment from "moment";

import { EditorContext } from "components/Editor";
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';

import styles from './componentStyles.module.scss';

const ContentConfigCard = ({ icon, name, mapping, gatingSetting, setGatingSetting, gatingConfigs, handleReset }) => {

  const iconUrl = _.get(icon, 'url', '') || _.get(icon, 'key', '');
  const isLocked = gatingConfigs.some(item => item.enabled) || false;
  const entityType = _.get(mapping,'content.entityType');

  const popover = (
    <Popover key={mapping.id} bsPrefix="popover" className={styles.assetGatingContainer}>
      <Popover.Body className={styles.assetGatingContainerBody}>
        <div className={styles.assetGatingContainerContent}>
          <span onClick={handleReset}><i className={cx("cleverstory-rotate-left")} />Reset to Original</span>
        </div>
        <AssetAliasSelector mapping={mapping} />
      </Popover.Body>
    </Popover>
  );

  return (
    <div className={cx(styles.actionCardContainer, { [styles.actionCardSlide]: gatingSetting })}>
      {icon && (
        <IKImage
          className={styles.icon}
          path={iconUrl}
        />)
      }
      {
        (!icon && name) &&  (<img src={'/images/storyboard.png'} className={styles.icon} />)
      }
      {
        (!icon && !name) && (<div className={styles.multipleDoc}><Image src={`${process.env.REACT_APP_UI_URL}/images/multidoc.svg`} className={styles.icon} /></div>)
      }
      <div className={styles.detailsWrapper}>
        <div className={styles.name}>
          <span className={styles.type}>Original Asset</span>
          <span className={styles.asset}>
            <Truncate lines={1}>
              { !name ? '-Multiple Asset-': name}
            </Truncate>
          </span>
        </div>
        <div className={styles.actions}>
          {entityType != "page" &&
            <Button onClick={() => setGatingSetting(!gatingSetting)} className={cx(styles.lockAction, { [styles.unlock]: !isLocked })} variant='info'>
              {isLocked ? <i className={cx('cleverstory-lock')} /> : <i className={cx('cleverstory-unlock')} />}
            </Button>
          }
          <OverlayTrigger
            rootClose={true}
            rootCloseEvent="mousedown"
            key={mapping.id}
            trigger="click"
            placement="bottom"
            overlay={popover}
          >
            <i className={cx('cleverstory-more', styles.iconMore)} />
          </OverlayTrigger>
        </div>
      </div>
    </div>
  )
}

const AssetAliasSelector = ({ mapping }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const [tooltip, setTooltip] = useState(false);

  const aliases = useSelector(state => _.get(state, 'content.aliases.items', []));
  const aliasesId = useSelector(state => _.get(state, 'content.aliases.id', null));
  const aliasesLoading = useSelector(state => _.get(state, 'content.aliases.loading', false));

  const contentType = _.get(mapping, "type");
  const url = _.get(mapping, 'url', null);
  const contentId = _.get(mapping, 'content.id', null);

  useEffect(() => {
    const filterRequest = {
      type: 'asset',
      target: contentId
    };

    dispatch(fetchAliasesForAsset(filterRequest, 0, 50));
  }, [contentId])

  useEffect(() => {
    if(aliasesId !== contentId){
      return
    }
    const urlExists = _.get(mapping, 'url', null);

    if(!urlExists && _.size(aliases) > 0) {
        const canonicalUrl = aliases.find(item => item.canonical)?.url;      
        handleAliasChange(canonicalUrl);
    }
  }, [contentId, aliases]);

  const handleAliasChange = (url) => {
    dispatch(updateAliasinLayoutContent(editorSession, contentId, url));
  }

  return (
    <div className={cx(styles.assetAliasContainerContent)}>
      <div>Asset Alias</div>
      {aliasesLoading ? (
        <div className={styles.assetAliasContainerSpinner}>
          <Spinner
            as="div"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          />
        </div>
      ) : (
        <Dropdown className={styles.aliasDropdown}>
          <Dropdown.Toggle
            as={Form.Group}
            id="dropdown-search"
            className={cx(styles.aliasDropdownToggle, {
              [styles.cursorAuto]: contentType !== "ASSET",
            })}
            disabled={contentType !== "ASSET"}
          >
            <TruncatedTextWithTooltip
              content={contentType == "ASSET" ? url : _.get(mapping, 'content.asset.canonical')}
              maxWidth="140"
              placement="top"
            />
            {contentType === "ASSET" && (
              <i className="cleverstory-drop-down" />
            )}
          </Dropdown.Toggle>
          {contentType === "ASSET" && aliases?.length > 0 &&
            <Dropdown.Menu className={styles.aliasMenu} onWheel={()=>setTooltip(false)}>
              {aliases.map((alias) => {
                const active = _.get(alias, "url") === url;
                return (
                  <Dropdown.Item
                    key={alias.id}
                    className={cx({ [styles.active]: active })}
                    onClick={() => handleAliasChange(alias.url)}
                  >
                    <div
                      className={cx(styles.details, {
                        [styles.active]: active,
                      })}
                    >
                      <div className={styles.metadata}>
                        <span className={styles.created}>
                          {moment(alias.modifiedDate).fromNow()}
                        </span>
                        {alias.canonical &&
                          <Badge className={styles.badge} bg="warning">
                            Canonical
                          </Badge>
                        }
                      </div>
                      <div className={styles.url}>
                        <Tooltip placement="top" content={_.get(alias, "url")} disabled={!tooltip}>
                          <Truncate lines={1} width={215} ellipsis="..." onTruncate={()=> setTooltip(true)}>
                            {_.get(alias, "url")}
                          </Truncate>
                        </Tooltip>
                      </div>
                      <div className={styles.author}>
                        Created by:{" "}
                        {`${_.get(alias, "createdBy.firstName")} ${_.get(
                          alias,
                          "createdBy.lastName"
                        )}`}
                      </div>
                    </div>
                  </Dropdown.Item>
                )
              })}
            </Dropdown.Menu>
          }
        </Dropdown>
      )}
    </div>
  )
}

export default ContentConfigCard